import { useCallback } from "react";

import toast from "react-hot-toast";

import {
  AssessmentEmailConsentDocument,
  AssessmentEmailConsentQuery,
  GroupFragmentDoc,
  useAssessmentEmailConsentQuery,
  useFinaliseGroupMutation,
  useGroupUpdatedSubscription,
  useJoinGroupMutation,
  useLeaveGroupMutation,
  useSetAssessmentEmailConsentMutation,
  WorkGroupDocument,
} from "@/generated/graphql";

/**
 * Hook to join a group in self enrolment groups.
 */
export function useJoinGroup(assessmentId: string) {
  const [joinGroupMutation] = useJoinGroupMutation();
  return useCallback(
    (groupId: string, onCompleted?: () => void) => {
      joinGroupMutation({
        variables: {
          assessmentId,
          groupId,
        },
        update: (cache) => {
          cache.evict({
            fieldName: "studentWorkGroup",
            args: { assessmentId },
          });
          cache.gc();
        },
        onCompleted: () => {
          onCompleted?.();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    },
    [joinGroupMutation, assessmentId]
  );
}

/**
 * Hook to leave a group in self enrolment groups.
 */
export function useLeaveGroup(assessmentId: string) {
  const [leaveGroupMutation] = useLeaveGroupMutation();
  return useCallback(
    (groupId: string, onCompleted?: () => void) => {
      leaveGroupMutation({
        variables: {
          assessmentId,
          groupId,
        },
        update: (cache) => {
          cache.evict({
            fieldName: "studentWorkGroup",
            args: { assessmentId },
          });
          cache.gc();
        },
        onCompleted: () => {
          onCompleted?.();
        },
        onError: () => {
          toast.error("Error in Leaving Group");
        },
      });
    },
    [leaveGroupMutation, assessmentId]
  );
}

/**
 * Hook to finalise the student's own group.
 */
export function useFinaliseGroup(assessmentId: string) {
  const [finaliseGroupMutation] = useFinaliseGroupMutation();
  return useCallback(
    (onCompleted?: () => void) => {
      finaliseGroupMutation({
        variables: {
          assessmentId,
        },
        refetchQueries: [
          { query: WorkGroupDocument, variables: { assessmentId } },
        ],
        onCompleted: () => {
          onCompleted?.();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    },
    [finaliseGroupMutation, assessmentId]
  );
}

/**
 * Hook to read and update the student's email sharing consent for a group
 * assessment.
 *
 * `setConsent` records a new consent event, updates the consent query cache
 * from the mutation result, and refreshes the queries carrying member emails
 * so email masking re-resolves.
 */
export function useEmailConsent(assessmentId: string) {
  const { data, loading } = useAssessmentEmailConsentQuery({
    variables: { assessmentId },
  });
  const [setAssessmentEmailConsentMutation] =
    useSetAssessmentEmailConsentMutation();

  const setConsent = useCallback(
    (consent: boolean, onError?: () => void) => {
      setAssessmentEmailConsentMutation({
        variables: {
          assessmentId,
          consent,
        },
        update: (cache, { data: result }) => {
          if (!result?.setAssessmentEmailConsent) {
            return;
          }
          cache.writeQuery<AssessmentEmailConsentQuery>({
            query: AssessmentEmailConsentDocument,
            variables: { assessmentId },
            data: {
              assessmentEmailConsent: result.setAssessmentEmailConsent,
            },
          });
          cache.evict({
            fieldName: "studentWorkGroup",
            args: { assessmentId },
          });
          // The self-enrolment group list carries member emails too, so it
          // must re-resolve against the new consent state.
          cache.evict({
            fieldName: "groupList",
            args: { assessmentId },
          });
          cache.gc();
        },
        refetchQueries: [
          { query: WorkGroupDocument, variables: { assessmentId } },
        ],
        onError: (error) => {
          toast.error(error.message);
          onError?.();
        },
      });
    },
    [setAssessmentEmailConsentMutation, assessmentId]
  );

  return {
    enabled: data?.assessmentEmailConsent.enabled ?? false,
    consent: data?.assessmentEmailConsent.consent ?? null,
    loading,
    setConsent,
  };
}

/**
 * Subscription to update the group list.
 */
export function useGroupUpdateSubscription(assessmentId: string) {
  return useGroupUpdatedSubscription({
    variables: { assessmentId },
    onData: ({ client, data: { data } }) => {
      if (data && data.groupUpdated) {
        const groupId = data.groupUpdated.id;
        client.writeFragment({
          id: `Group:${groupId}`,
          fragment: GroupFragmentDoc,
          fragmentName: "Group",
          data: data.groupUpdated,
        });
      }
    },
  });
}
